import React, { useState, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import theme from '../../themes/index.en';
import { CustomTextFieldProps } from './customTextFieldProps';
import {
  NOT_ALLOW_DECIMAL,
  NOT_ALLOW_DECIMAL_NEGATIVE,
  NOT_ALLOW_NEGATIVE,
  VALIDATION_ERROR_REQUIRED_MESSAGE,
} from '../../constants/actions';
import CustomIcon from '../customIcon/customIcon';
import GridStyles from '../../assets/css/grid.styles';

const CustomTextField: React.FC<CustomTextFieldProps> = ({
  label,
  name,
  type,
  value,
  onChange,
  onBlur,
  autoFocus,
  countryCode,
  required,
  error: propError,
  disabled,
  helperText: propHelperText,
  readOnly,
  InputProps,
  unit,
  isPhoneNumber,
  warningFlag,
  countryCodes = [],
  onCountryCodeChange,
  onClickOnIcon,
  numericInput = false,
  allowDecimal = false,
  allowNegative = true, 
  icon,
  isRTL = false
}) => {
  const [selectedCode, setSelectedCode] = useState<number>(countryCodes[0]?.id || 0);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [inputError, setInputError] = useState<boolean>(false);
  const [inputHelperText, setInputHelperText] = useState<string>('');
    
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newPhoneNumber = e.target.value; 
    let isError = false;
    let errorMessage = '';
    if (numericInput && newPhoneNumber ==='') {
      isError = true;
      newPhoneNumber = "0";
    }
    setInputError(isError);
    setInputHelperText(errorMessage);
    setPhoneNumber(newPhoneNumber);
    onChange &&
      onChange({
        target: { name, value: newPhoneNumber },
      } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleCountryCodeChange = (e: SelectChangeEvent<number>) => { 
    const newCode = e.target.value as number; 
    setSelectedCode(newCode);
    if (onCountryCodeChange) {
      onCountryCodeChange(newCode);
    }
  };

  useEffect(() => {
    if (countryCode) {
      setSelectedCode(countryCode);
    }
  }, [countryCode]);

  const removeCommas = (number: string) => number.replace(/,/g, '');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(!allowNegative && Number(e.target.value) < 0) return 
   
    let inputValue = numericInput 
    ? !allowDecimal ? (removeCommas(e.target.value).includes(".") ? e.target.value.split(".")[0] : removeCommas(e.target.value)) : removeCommas(e.target.value)
    :e.target.value
   
    let isError = false;
    let errorMessage = '';

    if (numericInput && inputValue ==='') {
      isError = true;
      inputValue = "0";
    }

    if (numericInput) {
      const regex = new RegExp(
        allowDecimal
          ? allowNegative
            ? /^-?\d*\.?\d*$/
            : /^\d*\.?\d*$/
          : allowNegative
            ? /^-?\d*$/
            : /^\d*$/
      );
 
      if (inputValue ==='') {
        isError = true;
        inputValue = "0";
      }
      if (!regex.test(inputValue)) {
        isError = true;
        errorMessage = allowNegative && !allowDecimal ? NOT_ALLOW_DECIMAL
        : !allowNegative && allowDecimal ? NOT_ALLOW_NEGATIVE
        : !allowNegative && !allowDecimal ? NOT_ALLOW_DECIMAL_NEGATIVE
        : '';
      }
    }
 
    setInputError(isError);
    setInputHelperText(errorMessage);
 
    onChange?.({ target: { name, value: inputValue }} as React.ChangeEvent<HTMLInputElement>);

  };
 

  const customLabel = (
    <Tooltip title={label} placement="top" arrow>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: isRTL ? 'flex-end' : 'flex-start', 
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {required && (
          <span
            style={{
              marginLeft: '5px', 
              color: theme.palette.error.main,
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            *
          </span>
        )}
      </span>
    </Tooltip> 
    );

  return (
    <FormControl
      fullWidth
      margin="normal"
      error={propError || inputError}
      sx={{
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left',
      }}
    >
      <TextField
        required={false}
        id={name}
        label={customLabel}
        name={name}
        autoComplete={name}
        type={type}
        value={value}
        onChange={isPhoneNumber ? handlePhoneNumberChange : handleInputChange}
        disabled={disabled}
        autoFocus={autoFocus}
        onBlur={onBlur}
        error={propError || inputError}
        helperText={propHelperText || inputHelperText}
        InputLabelProps={{
          shrink: true,
          sx: {
            textAlign: isRTL ? 'right' : 'left',
            right: isRTL ? 0 : 'auto', 
            left: isRTL ? 'auto' : 0, 
        },
        }}
        FormHelperTextProps={{
          style: { color: theme.palette.error.main, textAlign: isRTL ? 'right' : 'left' },
        }}
        InputProps={{
          ...InputProps,
          readOnly: readOnly,
          startAdornment: (
            <> 
              {isPhoneNumber&& !isRTL && (
                <InputAdornment position="start">
                  <Select
                    value={selectedCode}
                    onChange={handleCountryCodeChange}
                    disableUnderline
                    style={{
                      minWidth: '80px',
                      color: theme.palette.text.primary,
                      textAlign: 'right',
                      marginRight: '8px', 
                      marginLeft:  '0', 
                    }}
                    disabled={disabled || readOnly}
                    variant="standard"
                  >
                    {countryCodes.map((code) => (
                      <MenuItem key={code.id} value={code.id}>
                        {code.code}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              )}
            </>
          ),
          endAdornment:(
           <>
             {icon && 
             <InputAdornment position="start">
              {
                onClickOnIcon ? (
                  <CustomIcon
                  tooltipText="Password"
                  onClick={onClickOnIcon} 
                >
                  {icon}
                </CustomIcon>
                ) : (
                  icon
                )
              } 
             </InputAdornment>}
              {isPhoneNumber&& isRTL && (
                <InputAdornment position="start">
                  <Select
                    value={selectedCode}
                    onChange={handleCountryCodeChange}
                    disableUnderline
                    style={{
                      minWidth: '80px',
                      color: theme.palette.text.primary,
                      textAlign: 'left',
                      marginLeft: '8px', 
                      marginRight:  '0',  
                    }}
                    disabled={disabled || readOnly}
                    variant="standard"
                  >
                    {countryCodes.map((code) => (
                      <MenuItem key={code.id} value={code.id}>
                        {code.code}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              )}
              {unit && (
                <InputAdornment position="end">
                  <span style={{ color: theme.palette.primary.main }}>{unit}</span>
                </InputAdornment>
              )}
          </> 
          ),
        }}
        sx={{
          backgroundColor: warningFlag
            ? theme.palette.warning.light
            : theme.palette.background.default,
          direction: isRTL ? 'rtl' : 'ltr',
        }}
        fullWidth
      />
    </FormControl>
  );
};

export default CustomTextField;
